import flow from 'lodash.flow'
import { inject } from 'tsyringe'
import { containerScoped } from '~/decorators/dependency-container'
import {
  TreeLevelCategory,
  TreeLevelCategoryResult,
  TreeLevelGetterOptions
} from '~/models/category/types'
import { formatTreeLevelCategory } from '~/services/category/formatters'
import RequestBuilder from '~/builders/http/RequestBuilder'
import { toCamelCase } from '~/utils/object'

@containerScoped()
export default class CategoryTreeLevelService {
  constructor(@inject(RequestBuilder) private requestBuilder: RequestBuilder) {}

  getCategoryTreeLevels(
    rootCategoryIdOrIds: number | number[],
    categoryId?: number,
    options: TreeLevelGetterOptions = { rootUrlParam: true }
  ): Promise<TreeLevelCategoryResult> {
    const rootCategoryIds =
      typeof rootCategoryIdOrIds === 'number'
        ? [rootCategoryIdOrIds]
        : rootCategoryIdOrIds
    const rootCategoryId = options.rootUrlParam ? rootCategoryIds[0] : undefined
    return this.requestBuilder
      .method<TreeLevelCategoryResult>('get')
      .url(this.getGetEndpointName(categoryId, rootCategoryId))
      .params({ root: options.rootUrlParam ? undefined : rootCategoryIds })
      .map(body => this.formatCategoryLevelResponseBody(body))
      .send()
  }

  fetchRecentCategoryLevel(category?: number): Promise<TreeLevelCategory[]> {
    return this.requestBuilder
      .request<TreeLevelCategory[]>('get', '/api/categories-tree/recent/')
      .params({
        category
      })
      .validate(body => body?.data?.categories)
      .map(body =>
        flow(
          body => toCamelCase(body.data.categories),
          categories =>
            categories.map((c: TreeLevelCategory) => ({
              ...c,
              isRecent: true,
              humanName: c.humanPath
            }))
        )(body)
      )
      .send()
  }

  private formatCategoryLevelResponseBody(body: any): TreeLevelCategoryResult {
    return {
      levels: this.formatCategoryLevels(body.data.categories),
      hasRecentCategories: body.data.has_recent_categories
    }
  }

  private formatCategoryLevels(categoryLevels: any[][]): TreeLevelCategory[][] {
    return categoryLevels.map((level: any[]) =>
      level.map(formatTreeLevelCategory)
    )
  }

  private getGetEndpointName(
    categoryId: number | undefined,
    rootCategoryId?: number
  ): string {
    return typeof categoryId !== 'undefined' &&
      typeof rootCategoryId !== 'undefined'
      ? `/api/categories-tree/${rootCategoryId}/${categoryId}/`
      : typeof categoryId !== 'undefined'
      ? `/api/categories-tree/${categoryId}/`
      : '/api/categories-tree/'
  }
}
